// REST-клиент. Кадры чата идут через transport.ts, здесь всё остальное.

const TOKEN_KEY = 'ozarnik_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export class ApiError extends Error {
  // Поле объявлено явно: erasableSyntaxOnly в TS 6 запрещает
  // сокращение через параметр конструктора.
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const resp = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Бесплатный ngrok вклинивает HTML-страницу с предупреждением вместо
      // ответа, и тогда resp.json() падает, а приложение решает, что сервер
      // недоступен. Заголовок отключает эту вставку; вне ngrok он безвреден.
      'ngrok-skip-browser-warning': 'true',
      ...init.headers,
    },
  })

  if (!resp.ok) {
    let message = `сервер ответил ${resp.status}`
    try {
      const body = await resp.json()
      if (body?.error) message = body.error
    } catch {
      // тело не json — оставляем общий текст
    }
    throw new ApiError(message, resp.status)
  }
  return (await resp.json()) as T
}

// ── типы ────────────────────────────────────────────────────────────────────

export interface Health {
  ok: boolean
  models: string[]
  transcription: boolean
  bot_username: string
  anonymous: boolean
}

export interface Me {
  user_id: number
  user_name: string
  expires_at: number
  /** Ходить ли в интернет: настройка человека, одна на все треды. */
  web_enabled: boolean
}

export type ConversationMode = 'chat' | 'agent'

export interface Conversation {
  id: number
  title: string
  created_at: string
  active: boolean
  pinned: boolean
  mode: ConversationMode
}

export interface StoredMessage {
  role: 'user' | 'assistant'
  content: string
  author: string
}

export interface ModelsInfo {
  models: string[]
  default: string
  current: string | null
}

export interface UploadedFile {
  path: string
  name: string
}

// ── фоновые задачи ──────────────────────────────────────────────────────────

export interface Task {
  id: number
  goal: string
  state:
    | 'proposed'
    | 'queued'
    | 'running'
    | 'waiting_approval'
    | 'paused'
    | 'done'
    | 'failed'
    | 'cancelled'
  created_by: 'user' | 'model' | 'schedule'
  used_steps: number
  budget_steps: number
  used_tokens: number
  budget_tokens: number
  /** Когда возьмут в работу, если это ещё впереди. */
  starts_at?: string
  schedule_id?: number
  error?: string
}

export interface Schedule {
  id: number
  goal: string
  state: 'proposed' | 'active' | 'cancelled'
  every_hours: number
  next_run_at: string
  fired_count: number
}

/**
 * Запрос разрешения на вызов инструмента.
 *
 * `args` приходит целиком и показывается целиком: одобрить пересказ команды —
 * не то же самое, что одобрить команду. Если аргументы не помещаются, сервер
 * этот запрос вообще не отдаёт — он уже отклонён.
 */
export interface Approval {
  id: number
  task_id: number
  tool: string
  args: string
  expires_at: string
}

export interface TaskStep {
  idx: number
  intent: string
  tool: string
  ok: boolean | null
  observation: string
  duration_ms: number
}

export interface TaskEvent {
  id: number
  kind: string
  payload: unknown
  at: string
}

export interface TaskDetail {
  task: Task
  plan: string
  digest: string
  result: string
  steps: TaskStep[]
  events: TaskEvent[]
  /** Курсор для следующего запроса: отдать в `after`. */
  cursor: number
}

// ── ручки ───────────────────────────────────────────────────────────────────

export const api = {
  health: () => request<Health>('/health'),
  me: () => request<Me>('/me'),

  authTelegram: (fields: Record<string, unknown>) =>
    request<{ token: string; user_id: number; user_name: string }>(
      '/auth/telegram',
      { method: 'POST', body: JSON.stringify(fields) },
    ),

  /// Вход из мини-приложения: подписанная строка от Telegram, проверяется
  /// на сервере. Ручка отдельная от виджета — у них разные схемы подписи.
  authTelegramWebApp: (initData: string) =>
    request<{ token: string; user_id: number; user_name: string }>(
      '/auth/telegram-webapp',
      { method: 'POST', body: JSON.stringify({ init_data: initData }) },
    ),

  /// Отмена генерации. Один путь для обоих транспортов: у SSE нет
  /// обратного канала, чтобы попросить сервер остановиться.
  cancel: (convId: number | null) =>
    request<{ ok: boolean }>('/cancel', {
      method: 'POST',
      body: JSON.stringify({ conv_id: convId }),
    }),

  models: () => request<ModelsInfo>('/models'),
  setModel: (model: string) =>
    request<{ ok: boolean }>('/models', {
      method: 'POST',
      body: JSON.stringify({ model }),
    }),

  tasks: () =>
    request<{ tasks: Task[]; schedules: Schedule[]; approval: Approval | null }>(
      '/tasks',
    ),
  /**
   * Задача целиком. `after` — id последнего показанного кадра.
   *
   * Опрос, а не сокет: задачу почти всегда исполняет другой процесс (бот), и
   * дотянуться до него без брокера нельзя — а брокера в проекте нет намеренно.
   * Журнал `task_events` для этого и хранится, с курсором.
   */
  task: (id: number, after = 0) =>
    request<TaskDetail>(`/tasks/${id}?after=${after}`),
  confirmTask: (id: number) =>
    request<{ ok: boolean }>(`/tasks/${id}/confirm`, { method: 'POST' }),
  cancelTask: (id: number) =>
    request<{ ok: boolean }>(`/tasks/${id}/cancel`, { method: 'POST' }),
  confirmSchedule: (id: number) =>
    request<{ ok: boolean }>(`/schedules/${id}/confirm`, { method: 'POST' }),
  cancelSchedule: (id: number) =>
    request<{ ok: boolean }>(`/schedules/${id}/cancel`, { method: 'POST' }),
  decideApproval: (id: number, approve: boolean) =>
    request<{ ok: boolean; approved: boolean }>(`/approvals/${id}`, {
      method: 'POST',
      body: JSON.stringify({ approve }),
    }),

  conversations: () =>
    request<{ conversations: Conversation[] }>('/conversations').then(
      (r) => r.conversations,
    ),
  // Режим выбирается при создании: у Zed-подобной раскладки это две разные
  // кнопки «новый чат» и «агент», а не настройка, которую ищут потом.
  createConversation: (mode: ConversationMode = 'chat') =>
    request<{ id: number; mode: ConversationMode }>('/conversations', {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),

  setConversationMode: (id: number, mode: ConversationMode) =>
    request<{ ok: boolean; mode: ConversationMode }>(
      `/conversations/${id}/mode`,
      { method: 'POST', body: JSON.stringify({ mode }) },
    ),

  /** Интернет — переключатель про человека, а не про тред. */
  setWeb: (enabled: boolean) =>
    request<{ ok: boolean; enabled: boolean }>('/web', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),
  renameConversation: (id: number, title: string) =>
    request<{ ok: boolean }>(`/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),
  deleteConversation: (id: number) =>
    request<{ ok: boolean }>(`/conversations/${id}`, { method: 'DELETE' }),

  // Pinning is its own endpoint rather than a field on PATCH: renaming and
  // pinning are separate intents, triggered from different places in the UI.
  pinConversation: (id: number, pinned: boolean) =>
    request<{ ok: boolean; pinned: boolean }>(`/conversations/${id}/pin`, {
      method: 'POST',
      body: JSON.stringify({ pinned }),
    }),

  /// Уборка при выходе из диалога: сервер снесёт его, только если в нём
  /// ничего не написано. Решает сервер, а не клиент — тот может не знать
  /// про сообщение, пришедшее секунду назад.
  cleanupConversation: (id: number) =>
    request<{ ok: boolean; removed: boolean }>(`/conversations/${id}/cleanup`, {
      method: 'POST',
    }),

  /// То же самое, но для закрытия вкладки: обычный fetch там не успевает.
  /// `keepalive` разрешает браузеру доотправить запрос после выгрузки.
  cleanupConversationOnUnload: (id: number) => {
    const token = getToken()
    return fetch(`/api/conversations/${id}/cleanup`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      keepalive: true,
    }).catch(() => {})
  },

  messages: (id: number) =>
    request<{ messages: StoredMessage[] }>(
      `/conversations/${id}/messages`,
    ).then((r) => r.messages),

  upload: (files: File[], signal?: AbortSignal) => {
    const form = new FormData()
    for (const f of files) form.append('file', f)
    return request<{ files: UploadedFile[] }>('/upload', {
      method: 'POST',
      body: form,
      signal,
    }).then((r) => r.files)
  },

  fileUrl: (path: string) =>
    `/api/files/${path.split('/').map(encodeURIComponent).join('/')}`,

  downloadFile: async (path: string, name: string) => {
    const token = getToken()
    const resp = await fetch(api.fileUrl(path), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!resp.ok) throw new ApiError('не скачался', resp.status)
    const blob = await resp.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = name
    a.click()
    URL.revokeObjectURL(url)
  },

  /**
   * Asks the bot to put the file in the user's Telegram chat.
   *
   * Telegram's webview does not act on a synthetic `<a download>` of a blob,
   * so inside the Mini App the download button did nothing at all — not even
   * a request. The chat is where Telegram can save a file on its own.
   */
  sendFileToChat: (path: string) =>
    request<{ ok: boolean; name: string }>('/send-file', {
      method: 'POST',
      body: JSON.stringify({ path }),
    }),
}
